import { Settings } from '@ps/isp-web-component-library/icons/Settings';
import { FeatureFlag, Size } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation, useScreenSize } from '@ps/web-shared-components';
import { useLayout } from '@ps/web-sports-utils';
import React from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { IconLink, Menu, MenuVariant, TryAgainMessage } from '@swf/sports/components';
import useUrlMenuItems from '@swf/sports/hooks/useUrlMenuItems';

import {
  TXTCLI_Other__choice,
  TXTCLI_Settings,
  TXTWCL_A_Z_Sports,
  TXTWCL_Popular
} from './SportsMenuWidget.messages.json';
import styles from './SportsMenuWidget.scss';
import { SportsGlobalSearch } from './components/SportsGlobalSearch/SportsGlobalSearch';
import { sportsMenuWidgetModel, useSportsMenuWidget } from './model';

const SportsMenuWidget: WidgetFC = () => {
  const { showNavigationSection } = useLayout();
  const { greaterThan } = useScreenSize();
  const { formatMessage } = useRegulatoryIntl();
  const { widgetSelect } = useSportsMenuWidget();
  const az = useSelector(widgetSelect.az);
  const popular = useSelector(widgetSelect.popular);
  const azErrorCode = useSelector(widgetSelect.azErrorCode);
  const aliases = useSelector(select.cms.getAliases);
  const settingsRoute = aliases.sportsSettings;

  const popularMenuItems = useUrlMenuItems(popular);
  const azMenuItems = useUrlMenuItems(az);

  const isSettingsVisible = !greaterThan(Size.XXSmall);

  const ariaHidden = (showNavigationSection && greaterThan(Size.Small)) || greaterThan(Size.Large);
  const isSportsGlobalSearchEnabled = useFeature(FeatureFlag.ISPEnableGlobalSportsSearch);

  return (
    <>
      {isSportsGlobalSearchEnabled && <SportsGlobalSearch />}
      {popularMenuItems.length ? (
        <>
          <h2 className={styles.header}>
            <Translation {...TXTWCL_Popular} />
          </h2>
          <Menu>
            {popularMenuItems.map((item) => (
              <IconLink
                key={item.text}
                {...item}
                aria-hidden={ariaHidden}
                variant={MenuVariant.DARK}
              />
            ))}
          </Menu>
        </>
      ) : null}

      {azMenuItems.length || azErrorCode ? (
        <h2 className={styles.header}>
          <Translation {...TXTWCL_A_Z_Sports} />
        </h2>
      ) : null}

      {azMenuItems.length ? (
        <Menu>
          {azMenuItems.map((item) => (
            <IconLink
              key={item.text}
              {...item}
              aria-hidden={ariaHidden}
              variant={MenuVariant.DARK}
            />
          ))}
        </Menu>
      ) : null}

      {azErrorCode ? <TryAgainMessage size={Size.Small} /> : null}

      {isSettingsVisible && settingsRoute ? (
        <>
          <h2 className={styles.header}>
            <Translation {...TXTCLI_Other__choice} />
          </h2>

          <Menu>
            <IconLink
              icon={Settings}
              text={formatMessage(TXTCLI_Settings)}
              aria-hidden={ariaHidden}
              alias="sportsSettings"
              variant={MenuVariant.DARK}
              url={settingsRoute}
            />
          </Menu>
        </>
      ) : null}
    </>
  );
};

SportsMenuWidget.model = sportsMenuWidgetModel;

export default withWidgetTestId(SportsMenuWidget, 'sports-menu', {}, 'nav');
